import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { isDanielEntikCompany } from "../utils/entikRecordsAccess";

const STEPS = ["password", "company", "fiscal", "assistant"] as const;

type FieldErrors = Partial<Record<"password" | "companyName" | "companyCif", string>>;

function validateOnboardingPassword(password: string): string | null {
  if (!password || password.length < 10) {
    return "La contraseña debe tener al menos 10 caracteres";
  }
  if (!/[a-z]/.test(password)) return "La contraseña debe incluir una letra minúscula";
  if (!/[A-Z]/.test(password)) return "La contraseña debe incluir una letra mayúscula";
  if (!/[0-9]/.test(password)) return "La contraseña debe incluir un número";
  if (!/[!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]/.test(password)) {
    return "La contraseña debe incluir un carácter especial";
  }
  return null;
}

function validateCompanyStep(name: string, cif: string): FieldErrors {
  const fields: FieldErrors = {};
  const trimmedName = name.trim();
  const normalizedCif = cif.replace(/\s/g, "").toUpperCase();
  if (!trimmedName) fields.companyName = "El nombre de la empresa es obligatorio";
  if (!normalizedCif) {
    fields.companyCif = "El NIF/CIF es obligatorio";
  } else if (normalizedCif.startsWith("PEND-")) {
    fields.companyCif =
      "Introduce tu NIF/CIF real (no uses el valor provisional generado al crear la invitación)";
  } else if (normalizedCif.length < 8 || normalizedCif.length > 15) {
    fields.companyCif = "El NIF/CIF debe tener entre 8 y 15 caracteres";
  }
  return fields;
}

function stepForApiFields(fields?: Record<string, string>): (typeof STEPS)[number] | null {
  if (!fields) return null;
  if (fields.password) return "password";
  if (fields.companyName || fields.companyCif) return "company";
  return null;
}

export const OnboardingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<(typeof STEPS)[number]>("password");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyCif, setCompanyCif] = useState("");
  const [activity, setActivity] = useState("");
  const [defaultIva, setDefaultIva] = useState(21);
  const [defaultIrpf, setDefaultIrpf] = useState(15);
  const [assistantReply, setAssistantReply] = useState<string | null>(null);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [enableWooCommerce, setEnableWooCommerce] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Enlace no válido");
      setLoading(false);
      return;
    }
    (async () => {
      const res = await apiService.validateInviteToken(token);
      if (res.error) setError(res.message || res.error);
      else {
        const d = res.data as {
          user?: { email: string; onboardingCompleted?: boolean };
          company?: { name: string; cif: string };
        };
        if (d?.user?.onboardingCompleted) setError("Invitación ya usada. Inicia sesión.");
        else {
          setEmail(d?.user?.email || "");
          const cn = d?.company?.name || "";
          const cc = d?.company?.cif || "";
          if (!cn.startsWith("PEND")) setCompanyName(cn);
          if (!cc.startsWith("PEND")) setCompanyCif(cc);
        }
      }
      setLoading(false);
    })();
  }, [token]);

  const clearErrors = () => {
    setError(null);
    setFieldErrors({});
  };

  const applyApiError = (res: {
    message?: string;
    error?: string;
    fields?: Record<string, string>;
  }) => {
    const fields = res.fields;
    if (fields && Object.keys(fields).length) {
      setFieldErrors(fields as FieldErrors);
      const first = Object.values(fields)[0];
      setError(first || res.message || res.error || "Revisa los datos indicados");
      const targetStep = stepForApiFields(fields);
      if (targetStep) setStep(targetStep);
      return;
    }
    setFieldErrors({});
    setError(res.message || res.error || "No se pudo completar el onboarding");
    const msg = String(res.message || res.error || "");
    if (/contraseña|password/i.test(msg)) setStep("password");
    else if (/cif|nif|empresa/i.test(msg)) setStep("company");
  };

  const goPasswordNext = () => {
    clearErrors();
    if (password !== password2) {
      setFieldErrors({ password: "Las contraseñas no coinciden" });
      setError("Las contraseñas no coinciden");
      return;
    }
    const pwdErr = validateOnboardingPassword(password);
    if (pwdErr) {
      setFieldErrors({ password: pwdErr });
      setError(pwdErr);
      return;
    }
    setStep("company");
  };

  const goCompanyNext = () => {
    clearErrors();
    const fields = validateCompanyStep(companyName, companyCif);
    if (Object.keys(fields).length) {
      setFieldErrors(fields);
      setError(Object.values(fields)[0] || "Revisa los datos de la empresa");
      return;
    }
    setStep("fiscal");
  };

  const askAssistant = async () => {
    if (!token) return;
    setAssistantLoading(true);
    setAssistantReply(null);
    clearErrors();
    const res = await apiService.onboardingAssistant(
      token,
      activity
        ? `Actividad: ${activity}. Recomienda IVA/IRPF para FactuApp.`
        : "3 preguntas breves para configurar un autónomo en FactuApp",
      { activity }
    );
    setAssistantLoading(false);
    const data = res.data as { reply?: string; message?: string };
    const unavailable =
      "El asistente no está disponible ahora. Puede continuar e entrar en FactuApp.";
    setAssistantReply(
      data?.reply ||
        data?.message ||
        (res.error ? String(res.message || res.error) : unavailable) ||
        unavailable
    );
  };

  const finish = async () => {
    if (!token) return;
    clearErrors();
    if (password !== password2) {
      setFieldErrors({ password: "Las contraseñas no coinciden" });
      setError("Las contraseñas no coinciden");
      setStep("password");
      return;
    }
    const pwdErr = validateOnboardingPassword(password);
    if (pwdErr) {
      setFieldErrors({ password: pwdErr });
      setError(pwdErr);
      setStep("password");
      return;
    }
    const companyFields = validateCompanyStep(companyName, companyCif);
    if (Object.keys(companyFields).length) {
      setFieldErrors(companyFields);
      setError(Object.values(companyFields)[0] || "Revisa los datos de la empresa");
      setStep("company");
      return;
    }

    setSubmitting(true);
    const res = await apiService.completeOnboarding(token, {
      password,
      companyName: companyName.trim(),
      companyCif: companyCif.replace(/\s/g, "").toUpperCase(),
      activity,
      defaultIva,
      defaultIrpf,
      assistantAnswers: assistantReply ? { summary: assistantReply } : undefined,
      enableWooCommerce,
    });
    setSubmitting(false);
    if (res.error) {
      applyApiError(res);
      return;
    }
    const data = res.data as { token?: string };
    if (!data?.token) {
      setError("El servidor no devolvió la sesión. Inténtelo de nuevo o contacte al administrador.");
      return;
    }
    apiService.setToken(data.token);
    localStorage.setItem("authToken", data.token);
    navigate("/a", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-[system-ui,-apple-system,sans-serif]">
        <p className="text-zinc-500">Validando invitación…</p>
      </div>
    );
  }
  if (error && !email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 font-[system-ui,-apple-system,sans-serif]">
        <div className="text-center max-w-sm">
          <p className="text-red-600">{error}</p>
          <a href="/a" className="mt-4 inline-block text-blue-600 underline">
            Ir al login
          </a>
        </div>
      </div>
    );
  }

  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white py-10 px-4 font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif]">
      <div className="mx-auto max-w-lg rounded-2xl border border-zinc-200/80 bg-white/90 backdrop-blur-xl shadow-xl p-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Configura tu cuenta</h1>
        <p className="text-sm text-zinc-500 mt-1">{email}</p>

        <div className="mt-4 flex gap-1">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full ${i <= stepIndex ? "bg-blue-500" : "bg-zinc-200"}`}
            />
          ))}
        </div>

        {error && step === "assistant" && (
          <p className="text-sm text-red-600 mt-3 rounded-lg bg-red-50 px-3 py-2">{error}</p>
        )}

        {step === "password" && (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-zinc-600">
              Crea una contraseña segura (10+ caracteres, mayúscula, minúscula, número y símbolo).
            </p>
            <input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:outline-none ${
                fieldErrors.password ? "border-red-400" : "border-zinc-200"
              }`}
            />
            <input
              type="password"
              placeholder="Repetir contraseña"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:outline-none ${
                fieldErrors.password ? "border-red-400" : "border-zinc-200"
              }`}
            />
            {fieldErrors.password && (
              <p className="text-sm text-red-600">{fieldErrors.password}</p>
            )}
            <button
              type="button"
              className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-blue-500"
              onClick={goPasswordNext}
            >
              Continuar
            </button>
          </div>
        )}

        {step === "company" && (
          <div className="mt-5 space-y-3">
            <input
              placeholder="Nombre de la empresa"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm ${
                fieldErrors.companyName ? "border-red-400" : "border-zinc-200"
              }`}
            />
            {fieldErrors.companyName && (
              <p className="text-sm text-red-600 -mt-1">{fieldErrors.companyName}</p>
            )}
            <input
              placeholder="NIF/CIF"
              value={companyCif}
              onChange={(e) => setCompanyCif(e.target.value)}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm ${
                fieldErrors.companyCif ? "border-red-400" : "border-zinc-200"
              }`}
            />
            {fieldErrors.companyCif && (
              <p className="text-sm text-red-600 -mt-1">{fieldErrors.companyCif}</p>
            )}
            {error && !fieldErrors.companyName && !fieldErrors.companyCif && (
              <p className="text-sm text-red-600 rounded-lg bg-red-50 px-3 py-2">{error}</p>
            )}
            <button
              type="button"
              className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium"
              onClick={goCompanyNext}
            >
              Continuar
            </button>
          </div>
        )}

        {step === "fiscal" && (
          <div className="mt-5 space-y-3">
            <textarea
              placeholder="Describe tu actividad (opcional)"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              className="w-full border border-zinc-200 rounded-xl px-3 py-2.5 text-sm min-h-[80px]"
            />
            <div className="flex gap-4 text-sm text-zinc-600">
              <label>
                IVA %
                <input
                  type="number"
                  value={defaultIva}
                  onChange={(e) => setDefaultIva(Number(e.target.value))}
                  className="ml-2 w-14 border rounded-lg px-2 py-1"
                />
              </label>
              <label>
                IRPF %
                <input
                  type="number"
                  value={defaultIrpf}
                  onChange={(e) => setDefaultIrpf(Number(e.target.value))}
                  className="ml-2 w-14 border rounded-lg px-2 py-1"
                />
              </label>
            </div>
            {!isDanielEntikCompany({ id: "", cif: companyCif, name: companyName } as any) && (
              <label className="flex items-center gap-3 text-sm text-zinc-700 border border-violet-100 rounded-xl px-3 py-2 bg-violet-50/50">
                <input
                  type="checkbox"
                  checked={enableWooCommerce}
                  onChange={(e) => setEnableWooCommerce(e.target.checked)}
                  className="rounded text-violet-600"
                />
                Activar tienda online WooCommerce (plugin Entikmedia, sin claves API)
              </label>
            )}
            <button
              type="button"
              className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-medium"
              onClick={() => {
                clearErrors();
                setStep("assistant");
              }}
            >
              Continuar
            </button>
          </div>
        )}

        {step === "assistant" && (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-zinc-600">
              Opcional: el asistente te orienta con IVA/IRPF según tu actividad. No es el panel del
              dashboard — solo para este paso de bienvenida.
            </p>
            <button
              type="button"
              disabled={assistantLoading}
              onClick={askAssistant}
              className="w-full border border-zinc-200 rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-zinc-50 disabled:opacity-50"
            >
              <SparklesIcon className="h-4 w-4 text-violet-600" />
              {assistantLoading ? "Consultando…" : "Preguntar al asistente"}
            </button>
            {assistantReply && (
              <div className="text-sm bg-zinc-50 border border-zinc-100 p-3 rounded-xl max-h-36 overflow-y-auto whitespace-pre-wrap text-zinc-700">
                {assistantReply}
              </div>
            )}
            <button
              type="button"
              disabled={submitting}
              onClick={finish}
              className="w-full bg-emerald-600 text-white rounded-xl py-2.5 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
            >
              {submitting ? "Entrando…" : "Entrar en FactuApp"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
export default OnboardingPage;
